import React, { useState, useEffect, useRef } from "react";
import { Snackbar, Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";

/**
 * A generic toast component that can be used for various notifications
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the toast should be shown
 * @param {Function} props.onClose - Function to call when the toast should be closed
 * @param {Function} props.onAction - Function to call when the action button is clicked
 * @param {string} props.message - The message to display in the toast
 * @param {string} props.actionButtonText - Text for the action button
 * @param {Array<string>} props.excludedPaths - Paths where the toast should not be shown
 * @param {Function} props.checkCondition - Function that returns true if the toast should be shown
 * @param {number} props.delay - Delay in ms before showing the toast
 */
const ImportantToast = ({
  isOpen,
  onClose,
  onAction,
  message,
  actionButtonText = "Reconnect",
  excludedPaths = [],
  checkCondition = () => false,
  delay = 2000,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const timerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    // Don't show toast on excluded paths
    if (excludedPaths.includes(location.pathname)) {
      return;
    }

    if (!isOpen) {
      // Clear any existing timer first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set a new timer
      timerRef.current = setTimeout(() => {
        if (checkCondition()) {
          // skip
        } else {
          setOpen(true);
        }
      }, delay);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setOpen(false);
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, location.pathname, excludedPaths, checkCondition, delay]);

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    setOpen(false);
  };

  const handleClose = (event, reason) => {
    // Prevent closing when clicking outside
    if (reason === "clickaway") {
      return;
    }

    if (onClose) {
      onClose();
    }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      onClose={handleClose}
      autoHideDuration={null}
      sx={{
        top: "24px !important",
        zIndex: 1400,
        "& .MuiPaper-root": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          minWidth: "auto",
          borderRadius: "10px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2.5,
          py: 1,
          bgcolor: theme.palette.background.paper,
          borderRadius: "10px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 14, mr: 1 }}>
          {message}
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={handleAction}
          sx={{
            fontSize: 13,
            minWidth: 0,
            px: 1,
            py: 0.5,
            textTransform: "none",
          }}
        >
          {actionButtonText}
        </Button>
      </Box>
    </Snackbar>
  );
};

export default ImportantToast;
